import { useEffect, useState } from 'react';
import { Users, RefreshCw, AlertTriangle } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import { willEngineAPI } from '../services/api.js';

// ── WILL ENGINE SUBSCRIBERS ──────────────────────────────────────────────────
//
// Brian: "we need to have a user section that will show all the users that are
// signed up and information about their profile."
//
// ⛔ THIS READS THROUGH CORE, NOT WILL ENGINE DIRECTLY. Subscribers live in Will
// Engine's own database; core proxies with a service key held server-side. The
// browser never sees that key — a key shipped to a browser is a public one, and this
// reads every subscriber's name, number and plan.
//
// It is deliberately READ-ONLY. Changing a plan or cancelling happens in Stripe, which
// is the only place that can actually move money; a cancel button here would be a
// second source of truth for whether somebody is a customer.

const TIERS = {
  executive: 'Executive',
  executive_plus: 'Executive Plus',
  executive_prime: 'Executive Prime'
};

const STATUS_TONE = {
  active: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
  trialing: 'text-amber-300 border-amber-500/40 bg-amber-500/10',
  canceled: 'text-stone-400 border-stone-600/40 bg-stone-700/20',
  lead: 'text-sky-300 border-sky-500/40 bg-sky-500/10'
};

const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function Subscribers() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const r = await willEngineAPI.subscribers();
      setRows(r.subscribers || []);
      setError(null);
    } catch (err) {
      setRows([]);
      // A 503 here is "not wired up yet", which is a setup step rather than a fault.
      // Show the server's own sentence — it names the missing variable.
      setError(err?.body?.message || err.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, []);

  const paying = (rows || []).filter((r) => r.status === 'active' || r.status === 'trialing');
  const mrr = paying.reduce((n, r) => {
    if (r.product === 'coach') return n + 30;
    return n + ({ executive: 199, executive_plus: 349, executive_prime: 499 }[r.tier] || 0);
  }, 0);

  return (
    <div>
      <PageHeader
        title="Subscribers"
        subtitle="Everyone signed up to the Coach or the Executive Assistant"
        actions={
          <button onClick={load} disabled={busy}
            className="inline-flex items-center gap-2 rounded-full border border-stone-600/60 px-4 py-2 text-sm text-stone-200 hover:bg-stone-700/30 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        }
      />

      {error && <ErrorBanner message={error} />}

      {rows && rows.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat label="subscribers" value={rows.length} sub="every signup, including leads" />
          <Stat label="paying" value={paying.length} sub="active or trialing" />
          {/* Derived from the tier, not from Stripe — a headline figure, not an invoice. */}
          <Stat label="monthly run-rate" value={`$${mrr.toLocaleString()}`} sub="at list price" />
        </div>
      )}

      {rows === null ? (
        <p className="text-sm text-stone-400">Loading…</p>
      ) : rows.length === 0 && !error ? (
        <p className="text-sm text-stone-400">No signups yet.</p>
      ) : rows.length === 0 ? null : (
        <div className="overflow-x-auto rounded-xl border border-stone-700/50">
          <table className="w-full text-sm">
            <thead className="bg-stone-800/40 text-left text-xs uppercase tracking-wide text-stone-400">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Billing</th>
                <th className="px-4 py-3">Came from</th>
                <th className="px-4 py-3">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-stone-700/40">
                  <td className="px-4 py-3 text-stone-100">{r.firstName || '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-300">{r.phone || '—'}</td>
                  <td className="px-4 py-3 text-stone-300">
                    {r.product === 'coach' ? 'The Coach' : (TIERS[r.tier] || 'Executive Assistant')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_TONE[r.status] || STATUS_TONE.lead}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-400">{r.hasBilling ? 'on file' : '—'}</td>
                  <td className="px-4 py-3 text-stone-400">{r.influencer || r.refCode || 'direct'}</td>
                  <td className="px-4 py-3 text-stone-400">{when(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-6 flex items-start gap-2 text-xs text-stone-500">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Read-only. Plans and cancellations are changed in Stripe, which is the only place
        that moves money — a control here would be a second answer to whether somebody is
        a customer.
      </p>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-stone-700/50 bg-stone-800/20 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-stone-400">{label}</div>
      <div className="mt-1 text-2xl text-stone-100">{value}</div>
      <div className="mt-0.5 text-xs text-stone-500">{sub}</div>
    </div>
  );
}
